import React, { useCallback } from "react";
import PathPickMobileFilterModal from "@/features/pathpick/components/ui/PathPickMobileFilterModal";
import PathPickRangeFilterSection from "@/features/pathpick/components/ui/PathPickRangeFilterSection";
import usePathPickProductFilter, {
  PathPickProductFilter,
  PathPickProductFilterKey,
} from "@/features/pathpick/features/search-products/hooks/usePathPickProductFilter";
import usePathPickProductFilterGroups, {
  getPathPickProductFilterValues,
  togglePathPickProductFilterValue,
} from "@/features/pathpick/features/search-products/hooks/usePathPickProductFilterGroups";
import usePathPickProductSortOptions from "@/features/pathpick/features/search-products/hooks/usePathPickProductSortOptions";
import usePathPickFilterDraft from "@/features/pathpick/hooks/usePathPickFilterDraft";
import usePathPickProductCardView from "@/features/pathpick/hooks/usePathPickProductCardView";
import {
  CLEARED_PATH_PICK_RANGE_FILTER,
  getInvalidPathPickRangeFields,
  hasPathPickRangeFilter,
} from "@/features/pathpick/utils/range-filter";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

// 모바일 필터 버튼 배지 — 기본값이 아닌 필터 축의 수(모달 안에서 고를 수 있는 축만 센다).
export const countPathPickProductFilterAxes = (
  filter: PathPickProductFilter,
  isComparisonVisible: boolean
) =>
  [
    (filter.collection__in?.length ?? 0) > 0,
    (filter.brandId__in?.length ?? 0) > 0,
    (filter.pathradeCategory__in?.length ?? 0) > 0,
    hasPathPickRangeFilter(filter),
    (filter.orderBy ?? "recommended") !== "recommended",
    isComparisonVisible &&
      (filter.marketPriceBasis ?? "bidPrice") !== "bidPrice",
  ].filter(Boolean).length;

// 상품 찾기 모바일 필터 — 데스크톱 헤더의 상품 유형 줄·비교 기준·정렬·상세 필터를 한 모달에 모은다.
// 상품 유형·정렬·비교 기준까지 전부 드래프트로 들고 있다가 `필터 적용`에서 한 번에 반영한다.
// 열 때만 마운트되므로 드래프트는 열 때마다 적용값에서 다시 시작한다.
function PathPickSearchProductsProductListFilterModal({
  isOpen,
  onClose,
}: Props) {
  const { filter, apply, resetComparisonFilters } = usePathPickProductFilter();
  const groups = usePathPickProductFilterGroups();
  const sortOptions = usePathPickProductSortOptions();
  // 범위 필터는 크림·포이즌 비교 값으로 거르는 조건이라 비교 표시를 끄면 함께 감춘다.
  const { isComparisonVisible } = usePathPickProductCardView();
  const { draft, setDraftValues, updateDraft } =
    usePathPickFilterDraft<PathPickProductFilter>(filter);

  // 모달 안에서 비교 표시를 끄면 드래프트의 마진·거래 정렬이 옵션에서 사라질 수 있어 첫 옵션으로 보여준다.
  const sortValue =
    sortOptions.find((option) => option.value === draft.orderBy)?.value ??
    sortOptions[0].value;

  const handleToggle = useCallback(
    (key: PathPickProductFilterKey, value: string | number) => {
      updateDraft((current) =>
        togglePathPickProductFilterValue(current, key, value)
      );
    },
    [updateDraft]
  );

  // 검색어는 모달 밖(검색창)의 값이라 그대로 두고, 모달에서 고르는 축만 기본값으로 되돌린다.
  const handleReset = () => {
    setDraftValues({
      collection__in: undefined,
      brandId__in: undefined,
      pathradeCategory__in: undefined,
      ...CLEARED_PATH_PICK_RANGE_FILTER,
      orderBy: "recommended",
      marketPriceBasis: "bidPrice",
    });
  };

  // 바뀐 게 없으면 닫기만 한다 — apply는 항상 1페이지로 되돌려 보던 페이지를 잃는다.
  const handleApply = () => {
    const next = { ...draft, orderBy: sortValue };
    if (JSON.stringify(next) !== JSON.stringify(filter)) {
      apply(next);
    }
    onClose();
  };

  return (
    <PathPickMobileFilterModal
      isOpen={isOpen}
      onClose={onClose}
      sortOptions={sortOptions}
      sortValue={sortValue}
      onChangeSort={(orderBy) => setDraftValues({ orderBy })}
      marketPriceBasis={draft.marketPriceBasis ?? "bidPrice"}
      onChangeMarketPriceBasis={(marketPriceBasis) =>
        setDraftValues({ marketPriceBasis })
      }
      // 비교 표시를 끄면 사라진 조건을 URL에서도 치운다 — 바뀐 키의 드래프트 수정은 드래프트 훅이 버린다.
      onComparisonHidden={resetComparisonFilters}
      groups={groups}
      getSelectedValues={(key) => getPathPickProductFilterValues(draft, key)}
      onToggle={handleToggle}
      extraCells={
        isComparisonVisible ? (
          <PathPickRangeFilterSection value={draft} onChange={setDraftValues} />
        ) : undefined
      }
      onReset={handleReset}
      onApply={handleApply}
      isApplyDisabled={getInvalidPathPickRangeFields(draft).length > 0}
    />
  );
}

export default PathPickSearchProductsProductListFilterModal;
